import {useState, useRef, useEffect} from "react";
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import Image from "./Image";

const CalanderInput = ({
    id,
    register,
    field,
    type,
    placeholder,
    label,
    labelClass,
    subLabel,
    subLabelClass = 'p5-regular text-gray-500',
    error,
    onClick,
    onPaste,
    width,
    height,
    errorClass,
    showAsterisk,
    readOnly = false,
    wrapperClass='w-full',
    value: externalValue,
    onChange: externalOnChange,
    ...inputProps
}) => {
    const [isDateRangePickerOpen, setDateRagePickerOpen] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [typingValue, setTypingValue] = useState('');
    const [typingError, setTypingError] = useState('');
    const inputRef = useRef(null);

    // Support both Controller (field) and register patterns
    const isUsingController = !!field;
    const fieldValue = isUsingController ? field.value : externalValue;
    const fieldOnChange = isUsingController ? field.onChange : externalOnChange;

    // Only destructure register if it exists (not using Controller)
    const registerProps = !isUsingController && register ? register(id) : {};
    const { ref: registerRef, onChange: registerOnChange, ...restRegisterProps } = registerProps;

    const [internalValue, setInternalValue] = useState(fieldValue || '');

    const handleChange = fieldOnChange || registerOnChange;

    const convertToDisplayFormat = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB');
    };

    const convertToStorageFormat = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Sync internal value when external/field value changes (e.g. paste from parent)
    useEffect(() => {
        if (fieldValue !== internalValue) {
            setInternalValue(fieldValue);
        }
    }, [fieldValue]);

    // Extract dd/mm/yyyy segments — slash-aware to handle partial edits
    const getSegments = (formatted) => {
        if (formatted.includes('/')) {
            const parts = formatted.split('/');
            return {
                day: parts[0] || '',
                month: parts[1] || '',
                year: parts[2] || '',
            };
        }
        const digits = formatted.replace(/\D/g, '');
        return {
            day: digits.slice(0, 2),
            month: digits.slice(2, 4),
            year: digits.slice(4, 8),
        };
    };

    // Auto-format raw input into dd/mm/yyyy as user types
    const formatDateInput = (raw) => {
        // If the value already has slashes (e.g. user edited the day segment),
        // parse by slash boundaries to avoid digit-position misinterpretation.
        if (raw.includes('/')) {
            const parts = raw.split('/');
            const d = (parts[0] || '').replace(/\D/g, '').slice(0, 2);
            const mDigits = (parts[1] || '').replace(/\D/g, '');
            const m = mDigits.slice(0, 2);
            // Any overflow from month (>2 digits) spills into year, preserving auto-advance
            const y = (mDigits.slice(2) + (parts[2] || '').replace(/\D/g, '')).slice(0, 4);
            if (y.length > 0 || parts.length >= 3) return `${d}/${m}/${y}`;
            return `${d}/${m}`;
        }
        const digits = raw.replace(/\D/g, '');
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    };

    // Validate the formatted value progressively as the user types
    const validateTyping = (formatted) => {
        const { day: dayStr, month: monthStr, year: yearStr } = getSegments(formatted);

        if (dayStr.length >= 2) {
            const day = parseInt(dayStr, 10);
            if (day < 1 || day > 31) return 'Day must be between 01 and 31';
        }

        if (monthStr.length >= 2) {
            const month = parseInt(monthStr, 10);
            if (month < 1 || month > 12) return 'Month must be between 01 and 12';
        }

        if (dayStr.length === 2 && monthStr.length === 2 && yearStr.length === 4) {
            const day = parseInt(dayStr, 10);
            const month = parseInt(monthStr, 10) - 1;
            const year = parseInt(yearStr, 10);
            const date = new Date(year, month, day);
            if (
                isNaN(date.getTime()) ||
                date.getFullYear() !== year ||
                date.getMonth() !== month ||
                date.getDate() !== day
            ) {
                return 'Invalid date';
            }
        }

        return '';
    };

    // Parse pasted text supporting multiple common date formats
    const parsePastedValue = (str) => {
        if (!str) return null;
        const s = str.trim();

        // dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy (and single-digit day/month)
        let m = s.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
        if (m) {
            const day = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            const year = parseInt(m[3], 10);
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime()) && date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
                return date;
            }
        }

        // yyyy-mm-dd, yyyy/mm/dd (ISO-like)
        m = s.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/);
        if (m) {
            const year = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            const day = parseInt(m[3], 10);
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime()) && date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
                return date;
            }
        }

        return null;
    };

    const handleInputPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const parsed = parsePastedValue(pasted);
        if (parsed) {
            const storageDate = convertToStorageFormat(parsed);
            const display = convertToDisplayFormat(storageDate);
            setTypingValue(display);
            setTypingError(validateTyping(display));
            setInternalValue(storageDate);
            if (handleChange) handleChange(storageDate);
        }
    };

    const handleInputFocus = () => {
        setIsTyping(true);
        setTypingValue(convertToDisplayFormat(internalValue));
        setTypingError('');
    };

    const handleInputChange = (e) => {
        const formatted = formatDateInput(e.target.value);
        setTypingValue(formatted);
        setTypingError(validateTyping(formatted));
    };

    const handleInputBlur = () => {
        setIsTyping(false);
        const { day: dayStr, month: monthStr, year: yearStr } = getSegments(typingValue);
        const hasNoInput = dayStr === '' && monthStr === '' && yearStr === '';

        if (hasNoInput) {
            if (internalValue) {
                // User cleared the field (it had a value before)
                setInternalValue('');
                if (handleChange) handleChange('');
            }
        } else if (dayStr.length === 2 && monthStr.length === 2 && yearStr.length === 4 && !typingError) {
            // Full valid date — save to form
            const day = parseInt(dayStr, 10);
            const month = parseInt(monthStr, 10) - 1;
            const year = parseInt(yearStr, 10);
            const date = new Date(year, month, day);
            const storageDate = convertToStorageFormat(date);
            setInternalValue(storageDate);
            if (handleChange) handleChange(storageDate);
        }
        // Incomplete / invalid input — silently revert to stored value on display

        setTypingError('');
        if (isUsingController && field.onBlur) field.onBlur();
    };

    const displayError = isTyping ? typingError : (error?.message || '');
    const hasError = isTyping ? !!typingError : !!error;

    return (
        <div className={`flex items-center justify-start gap-x-2 ${wrapperClass}`}>
            <div className="w-full">
                {label && type !== 'hidden' && (
                    <label
                        className={`block w-full text-left text-gray-900 ${labelClass}`}
                    >
                        {label}{subLabel && <span className={subLabelClass}> {subLabel}</span>}{showAsterisk ? <span className="text-semantic-error-base"> *</span> : ''}
                    </label>
                )}
                <input
                    {...restRegisterProps}
                    {...(isUsingController && field)}
                    ref={(e) => {
                        if (isUsingController && field.ref) {
                            field.ref(e);
                        } else if (registerRef) {
                            registerRef(e);
                        }
                        inputRef.current = e;
                    }}
                    id={id}
                    {...inputProps}
                    type={type}
                    onClick={onClick}
                    onPaste={handleInputPaste}
                    className={` border-[1.5px] placeholder:text-gray-400 p5-regular ${inputProps.disabled ? 'bg-gray-100 text-gray-400' : 'text-basicBlack'} ${hasError ? 'border-semantic-error-base' : 'border-gray-300'
                        } ${inputProps.border ? inputProps.border : 'border-1'} ${width ? width : 'w-full'} ${height ? height : 'h-12'
                        } px-4 py-3 rounded-lg mt-2 box-border ${inputProps.className} ${inputProps?.disabled ? 'bg-gray-100' : 'bg-basicWhite cursor-pointer '}`}
                    placeholder={placeholder || 'DD/MM/YYYY'}
                    readOnly={readOnly}
                    value={isTyping ? typingValue : convertToDisplayFormat(internalValue)}
                    autoComplete="off"
                    maxLength={10}
                    onFocus={handleInputFocus}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                />
                {displayError && type !== 'hidden' && (
                    <div className={`text-semantic-error-base text-left ${errorClass}`}>{displayError}</div>
                )}
            </div>
            <div
                className={`${inputProps?.disabled ? 'bg-gray-100' : 'bg-basicWhite cursor-pointer '} ${hasError ? 'mt-0' : 'mt-7'} rounded-md border-[1.5px] border-gray-300 !w-12 !h-12 flex items-center justify-center relative`}
                onClick={() => {
                    if (!inputProps.disabled) {
                        setDateRagePickerOpen(true);
                    }
                }}
            >
                <Image
                    src="/images/symbols/Calendar.svg"
                    alt="calendar"
                    width="24"
                    height="24"
                    className="text-gray-400 w-6 h-6"
                />
                {isDateRangePickerOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-40"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDateRagePickerOpen(false);
                            }}
                        />
                        <div
                            className="absolute right-0 top-full mt-2 z-50 bg-white shadow-lg rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Calendar
                                date={internalValue ? new Date(internalValue) : new Date()}
                                onChange={(date) => {
                                    const storageDate = convertToStorageFormat(date);
                                    setInternalValue(storageDate);
                                    if (handleChange) {
                                        handleChange(storageDate);
                                    }
                                    setDateRagePickerOpen(false);
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
export default CalanderInput;
